import React from 'react';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { TagManager } from '@/components/ui';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { Button } from '@/components/ui/button';
import { Save, Loader2 } from 'lucide-react';
import { BaseBasicInfoProps } from './types';

export function BasicInfoDocument(props: BaseBasicInfoProps) {
  const { t } = usePlatformTranslation();

  const {
    editedData,
    setEditedData,
    isEditing,
    setIsEditing,
    isSaving,
    availableStatuses,
    permissions,
    onSave,
    onCancel,
  } = props;

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Basic Information
            </h3>
          </div>

          <p className="text-sm text-muted-foreground italic">
            Documents currently support tags and status only. More fields coming soon.
          </p>

          {/* Tags */}
          <div>
            <Label htmlFor="basic-info-tags" className="text-sm">
              {t('platform.common.tags')}
            </Label>
            <TagManager
              tags={editedData?.tags || []}
              onTagsChange={(tags) => {
                if (!isEditing) setIsEditing(true);
                setEditedData({ ...editedData, tags });
              }}
              disabled={!permissions.canEdit}
              className="mt-1"
            />
          </div>

          {/* Status */}
          {Object.keys(availableStatuses).length > 0 && (
            <div className="relative z-[100]">
              <Label htmlFor="basic-info-status" className="text-sm">Status</Label>
              <Select
                value={editedData?.status || Object.keys(availableStatuses)[0]}
                onValueChange={(value) => {
                  if (!isEditing) setIsEditing(true);
                  setEditedData({ ...editedData, status: value });
                }}
                disabled={!permissions.canEdit}
              >
                <SelectTrigger className="mt-1 border border-grey-G5 rounded p-2 text-sm font-medium text-grey-G2">
                  <SelectValue placeholder="Select status">
                    {editedData?.status && availableStatuses[editedData.status] && (
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full border border-border flex-shrink-0" 
                          style={{ backgroundColor: availableStatuses[editedData.status].color }}
                        />
                        <span className="capitalize">{editedData.status}</span>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="z-[100]" position="popper" side="top" sideOffset={4}>
                  {Object.entries(availableStatuses).map(([statusKey, statusData]) => (
                    <SelectItem key={statusKey} value={statusKey}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full border border-border flex-shrink-0" 
                          style={{ backgroundColor: statusData.color }}
                        />
                        <span className="capitalize">{statusKey}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Save/Cancel buttons */}
      {permissions.canEdit && isEditing && (
        <div className="p-3 border-t border-border bg-muted flex gap-2">
          <Button
            onClick={onSave}
            disabled={isSaving}
            className="flex-1"
            variant="default"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
          <Button
            onClick={onCancel}
            disabled={isSaving}
            variant="outline"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
